import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CarePlanSectionProps, RESIDENT_CARE_PLAN_TYPE } from "../../../constants/constants";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import ContactSection from "./contactSection";

const contactGridColumns = "1.2fr 1fr 1fr 1fr 1fr 1.2fr auto";

type GeneratedContactType = "physician" | "pharmacy" | "hospital";

const ResponsiblePartiesSection: React.FC<CarePlanSectionProps> = ({
  sectionData, submitButtonClick, backButtonClick, nextButtonClick, showHelpMessage
}) => {
  const mobile = useResponsive().isMobile;
  const [showAddContactDialog, setShowAddContactDialog] = useState<boolean>(false);
  const [contactsData, setContactsData] = useState<any[]>(sectionData?.collateralContacts || []);
  const [editingContactIndex, setEditingContactIndex] = useState<number | null>(null);
  const [editingGeneratedContactType, setEditingGeneratedContactType] = useState<GeneratedContactType | null>(null);

  const toast = useRef<Toast>(null);

  useEffect(() => {
    setContactsData(sectionData?.collateralContacts || []);
  }, [sectionData?.collateralContacts]);

  const generatedContacts = useMemo(() => ([
    {
      generated: true,
      generatedType: "physician" as GeneratedContactType,
      fullName: String(sectionData?.primaryPhysicianName || "").trim(),
      relationship: "Primary Physician",
      phoneNumber: String(sectionData?.primaryPhysicianPhone || sectionData?.primaryPhysicianPhoneNumber || "").trim(),
      faxNumber: String(sectionData?.primaryPhysicianFax || sectionData?.primaryPhysicianFaxNumber || "").trim(),
      email: String(sectionData?.primaryPhysicianEmail || "").trim(),
      preferredContactMethod: String(sectionData?.primaryPhysicianClinic || "").trim(),
      address: String(sectionData?.primaryPhysicianAddress || "").trim(),
    },
    {
      generated: true,
      generatedType: "pharmacy" as GeneratedContactType,
      fullName: String(sectionData?.pharmacyName || "").trim(),
      relationship: "Pharmacy",
      phoneNumber: String(sectionData?.pharmacyPhone || sectionData?.pharmacyPhoneNumber || "").trim(),
      faxNumber: String(sectionData?.pharmacyFax || sectionData?.pharmacyFaxNumber || "").trim(),
      email: String(sectionData?.pharmacyEmail || "").trim(),
      preferredContactMethod: String(sectionData?.pharmacyPreferredContactMethod || "").trim(),
      address: String(sectionData?.pharmacyAddress || "").trim(),
    },
    {
      generated: true,
      generatedType: "hospital" as GeneratedContactType,
      fullName: String(sectionData?.preferredHospitalName || "").trim(),
      relationship: "Preferred Hospital",
      phoneNumber: String(sectionData?.preferredHospitalPhone || sectionData?.preferredHospitalPhoneNumber || "").trim(),
      faxNumber: String(sectionData?.preferredHospitalFax || sectionData?.preferredHospitalFaxNumber || "").trim(),
      email: String(sectionData?.preferredHospitalEmail || "").trim(),
      preferredContactMethod: "Hospital",
      address: String(sectionData?.preferredHospitalLocation || "").trim(),
    },
  ].filter((contact) => (
    contact.fullName
    || contact.phoneNumber
    || contact.faxNumber
    || contact.email
    || contact.address
    || contact.preferredContactMethod
  ))), [
    sectionData?.pharmacyAddress,
    sectionData?.pharmacyEmail,
    sectionData?.pharmacyFax,
    sectionData?.pharmacyFaxNumber,
    sectionData?.pharmacyName,
    sectionData?.pharmacyPhone,
    sectionData?.pharmacyPhoneNumber,
    sectionData?.preferredHospitalEmail,
    sectionData?.preferredHospitalFax,
    sectionData?.preferredHospitalFaxNumber,
    sectionData?.preferredHospitalLocation,
    sectionData?.preferredHospitalName,
    sectionData?.preferredHospitalPhone,
    sectionData?.preferredHospitalPhoneNumber,
    sectionData?.primaryPhysicianAddress,
    sectionData?.primaryPhysicianClinic,
    sectionData?.primaryPhysicianEmail,
    sectionData?.primaryPhysicianFax,
    sectionData?.primaryPhysicianFaxNumber,
    sectionData?.primaryPhysicianName,
    sectionData?.primaryPhysicianPhone,
    sectionData?.primaryPhysicianPhoneNumber,
  ]);

  const allContacts = [...generatedContacts, ...contactsData];

  const closeGeneratedContactDialog = () => {
    setEditingGeneratedContactType(null);
  };

  const openGeneratedContactDialog = (type: GeneratedContactType) => {
    setEditingContactIndex(null);
    setEditingGeneratedContactType(type);
  };

  const generatedContactSectionData = editingGeneratedContactType === "physician"
    ? {
      fullName: String(sectionData?.primaryPhysicianName || "").trim(),
      relationship: "Primary Physician",
      phoneNumber: String(sectionData?.primaryPhysicianPhone || sectionData?.primaryPhysicianPhoneNumber || "").trim(),
      faxNumber: String(sectionData?.primaryPhysicianFax || sectionData?.primaryPhysicianFaxNumber || "").trim(),
      email: String(sectionData?.primaryPhysicianEmail || "").trim(),
      address: String(sectionData?.primaryPhysicianAddress || "").trim(),
      preferredContactMethod: String(sectionData?.primaryPhysicianClinic || "").trim(),
    }
    : editingGeneratedContactType === "pharmacy"
      ? {
        fullName: String(sectionData?.pharmacyName || "").trim(),
        relationship: "Pharmacy",
        phoneNumber: String(sectionData?.pharmacyPhone || sectionData?.pharmacyPhoneNumber || "").trim(),
        faxNumber: String(sectionData?.pharmacyFax || sectionData?.pharmacyFaxNumber || "").trim(),
        email: String(sectionData?.pharmacyEmail || "").trim(),
        address: String(sectionData?.pharmacyAddress || "").trim(),
        preferredContactMethod: String(sectionData?.pharmacyPreferredContactMethod || "").trim(),
      }
      : {
        fullName: String(sectionData?.preferredHospitalName || "").trim(),
        relationship: "Preferred Hospital",
        phoneNumber: String(sectionData?.preferredHospitalPhone || sectionData?.preferredHospitalPhoneNumber || "").trim(),
        faxNumber: String(sectionData?.preferredHospitalFax || sectionData?.preferredHospitalFaxNumber || "").trim(),
        email: String(sectionData?.preferredHospitalEmail || "").trim(),
        address: String(sectionData?.preferredHospitalLocation || "").trim(),
        preferredContactMethod: String(sectionData?.preferredHospitalPreferredContactMethod || "").trim(),
      };

  const onSubmit = async (data: any, index?: number) => {
    let updatedContacts;
    if (index !== undefined) {
      updatedContacts = contactsData.map((contact, idx) => idx === index ? data : contact);
      setContactsData(updatedContacts);
    } else {
      updatedContacts = [...contactsData, data];
      setContactsData(updatedContacts);
    }

    const updatedData = {
      ...sectionData,
      collateralContacts: updatedContacts,
      carePlanType: RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES,
    };
    submitButtonClick && submitButtonClick(updatedData);
    setShowAddContactDialog(false);
    setEditingContactIndex(null);
  };

  const onSubmitGeneratedContact = (data: any) => {
    if (!editingGeneratedContactType) {
      return;
    }

    const normalizedData = Object.entries(data || {}).reduce<Record<string, string>>((accumulator, [key, value]) => {
      accumulator[key] = String(value || "").trim();
      return accumulator;
    }, {});

    const updatedData: any = {
      ...sectionData,
      collateralContacts: contactsData,
      carePlanType: RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES,
    };

    if (editingGeneratedContactType === "physician") {
      updatedData.primaryPhysicianName = normalizedData.fullName || "";
      updatedData.primaryPhysicianClinic = normalizedData.preferredContactMethod || "";
      updatedData.primaryPhysicianPhone = normalizedData.phoneNumber || "";
      updatedData.primaryPhysicianFax = normalizedData.faxNumber || "";
      updatedData.primaryPhysicianEmail = normalizedData.email || "";
      updatedData.primaryPhysicianAddress = normalizedData.address || "";
    } else if (editingGeneratedContactType === "pharmacy") {
      updatedData.pharmacyName = normalizedData.fullName || "";
      updatedData.pharmacyPhone = normalizedData.phoneNumber || "";
      updatedData.pharmacyFax = normalizedData.faxNumber || "";
      updatedData.pharmacyEmail = normalizedData.email || "";
      updatedData.pharmacyAddress = normalizedData.address || "";
      updatedData.pharmacyPreferredContactMethod = normalizedData.preferredContactMethod || "";
    } else {
      updatedData.preferredHospitalName = normalizedData.fullName || "";
      updatedData.preferredHospitalPhone = normalizedData.phoneNumber || "";
      updatedData.preferredHospitalFax = normalizedData.faxNumber || "";
      updatedData.preferredHospitalEmail = normalizedData.email || "";
      updatedData.preferredHospitalLocation = normalizedData.address || "";
      updatedData.preferredHospitalPreferredContactMethod = normalizedData.preferredContactMethod || "";
    }

    submitButtonClick && submitButtonClick(updatedData);
    closeGeneratedContactDialog();
  };

  const onDeleteContact = (_data: any, index?: number) => {
    const updatedContactsList = contactsData.filter((_contact, idx) => idx !== index);
    setContactsData(updatedContactsList);
    submitButtonClick && submitButtonClick({
      ...sectionData,
      collateralContacts: updatedContactsList,
      carePlanType: RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES,
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component resident-careplan-section">
      <Toast ref={toast}></Toast>

      <Card
        className="resident-careplan-card"
        subTitle="Add those involved in care planning for your resident: Case Manager, DPOA, Guardian, Medical/Mental Health Providers, Pharmacy, Hospice, etc."
        footer={(
          <div className="flex justify-content-center gap-2">
            <Button label="Prev" icon="pi pi-arrow-left" severity="warning" outlined rounded onClick={backButtonClick} />
            <Button label="Next" icon="pi pi-arrow-right" severity="info" outlined rounded iconPos="right" onClick={nextButtonClick} />
          </div>
        )}
      >
        <div className="flex-column flex-wrap ">
          <Divider><Tag severity="warning">Contact details</Tag></Divider>
          <div className="flex flex-wrap justify-content-center align-items-center">
            {allContacts && allContacts.length > 0 ? (
              <div style={{ width: "100%", overflowX: "auto" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: mobile ? "repeat(2, minmax(180px, 1fr))" : contactGridColumns,
                    gap: "0.75rem",
                    alignItems: "stretch",
                    minWidth: mobile ? undefined : "1100px",
                  }}
                >
                  {!mobile ? (
                    <>
                      <div className="font-semibold text-700">Full name</div>
                      <div className="font-semibold text-700">Relationship</div>
                      <div className="font-semibold text-700">Phone</div>
                      <div className="font-semibold text-700">Fax</div>
                      <div className="font-semibold text-700">Email</div>
                      <div className="font-semibold text-700">Preferred contact</div>
                      <div className="font-semibold text-700 text-center">Actions</div>
                    </>
                  ) : null}

                  {allContacts.map((contact, index) => {
                    const contactIsGenerated = !!contact?.generated;
                    const collateralIndex = contactIsGenerated ? -1 : index - generatedContacts.length;
                    return (
                      <React.Fragment key={`${contact?.relationship || "contact"}-${contact?.fullName || index}-${index}`}>
                        <div style={{ border: "1px solid #dbe7f3", borderRadius: "12px", padding: "0.75rem", background: index % 2 === 0 ? "#f8fbff" : "#ffffff" }}>
                          {mobile ? <div className="font-semibold text-700 mb-2">Full name</div> : null}
                          <div className="flex align-items-center gap-2 flex-wrap">
                            <span>{contact?.fullName || "-"}</span>
                          </div>
                        </div>
                        <div style={{ border: "1px solid #dbe7f3", borderRadius: "12px", padding: "0.75rem", background: index % 2 === 0 ? "#f8fbff" : "#ffffff" }}>
                          {mobile ? <div className="font-semibold text-700 mb-2">Relationship</div> : null}
                          {contact?.relationship || "-"}
                        </div>
                        <div style={{ border: "1px solid #dbe7f3", borderRadius: "12px", padding: "0.75rem", background: index % 2 === 0 ? "#f8fbff" : "#ffffff" }}>
                          {mobile ? <div className="font-semibold text-700 mb-2">Phone</div> : null}
                          {contact?.phoneNumber || "-"}
                        </div>
                        <div style={{ border: "1px solid #dbe7f3", borderRadius: "12px", padding: "0.75rem", background: index % 2 === 0 ? "#f8fbff" : "#ffffff" }}>
                          {mobile ? <div className="font-semibold text-700 mb-2">Fax</div> : null}
                          {contact?.faxNumber || "-"}
                        </div>
                        <div style={{ border: "1px solid #dbe7f3", borderRadius: "12px", padding: "0.75rem", background: index % 2 === 0 ? "#f8fbff" : "#ffffff", wordBreak: "break-word" }}>
                          {mobile ? <div className="font-semibold text-700 mb-2">Email</div> : null}
                          {contact?.email || "-"}
                        </div>
                        <div style={{ border: "1px solid #dbe7f3", borderRadius: "12px", padding: "0.75rem", background: index % 2 === 0 ? "#f8fbff" : "#ffffff" }}>
                          {mobile ? <div className="font-semibold text-700 mb-2">Preferred contact</div> : null}
                          {contact?.preferredContactMethod || "-"}
                          {contact?.address ? (
                            <div style={{ marginTop: "0.5rem", color: "#5f7488" }}>
                              <strong>Address:</strong> {contact.address}
                            </div>
                          ) : null}
                        </div>
                        <div style={{ border: "1px solid #dbe7f3", borderRadius: "12px", padding: "0.75rem", background: index % 2 === 0 ? "#f8fbff" : "#ffffff", display: "flex", justifyContent: "center", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                          {mobile ? <div className="font-semibold text-700 mb-2 w-full text-center">Actions</div> : null}
                          {contactIsGenerated ? (
                            <Button
                              label="Edit"
                              icon="pi pi-pencil"
                              severity="warning"
                              outlined
                              rounded
                              onClick={() => openGeneratedContactDialog(contact.generatedType)}
                            />
                          ) : (
                            <>
                              <Button
                                label="Edit"
                                icon="pi pi-pencil"
                                severity="warning"
                                outlined
                                rounded
                                onClick={() => {
                                  setEditingGeneratedContactType(null);
                                  setEditingContactIndex(collateralIndex);
                                  setShowAddContactDialog(true);
                                }}
                              />
                              <Button
                                label="Delete"
                                icon="pi pi-trash"
                                severity="danger"
                                outlined
                                rounded
                                onClick={() => onDeleteContact(contact, collateralIndex)}
                              />
                            </>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            ) : (
              <span className="text-900">No contacts added yet.</span>
            )}
          </div>
          <div className="flex justify-content-center mt-4">
            <Button
              label="Add contact"
              icon="pi pi-plus"
              severity="success"
              outlined
              rounded
              onClick={(event) => {
                event.preventDefault();
                setEditingGeneratedContactType(null);
                setEditingContactIndex(null);
                setShowAddContactDialog(true);
              }}
            />
          </div>
        </div>
      </Card>

      <Dialog
        header={editingContactIndex !== null ? "Edit Contact" : "Add Contact"}
        visible={showAddContactDialog}
        style={{ width: mobile ? "92%" : "70%" }}
        modal
        onHide={() => {
          setShowAddContactDialog(false);
          setEditingContactIndex(null);
        }}
      >
        <div className="p-fluid">
          <ContactSection
            uniqueKey={editingContactIndex ?? undefined}
            sectionData={editingContactIndex !== null ? contactsData[editingContactIndex] || {} : {}}
            enableEditing={true}
            submitButtonClick={onSubmit}
            deleteButtonClick={onDeleteContact}
            showHelpMessage={showHelpMessage}
          />
        </div>
      </Dialog>

      <Dialog
        header={
          editingGeneratedContactType === "physician"
            ? "Edit Primary Physician"
            : editingGeneratedContactType === "pharmacy"
              ? "Edit Pharmacy"
              : "Edit Preferred Hospital"
        }
      visible={!!editingGeneratedContactType}
      style={{ width: mobile ? "92%" : "60%" }}
      modal
      onHide={closeGeneratedContactDialog}
    >
      <div className="p-fluid">
        <ContactSection
          sectionData={generatedContactSectionData}
          enableEditing={true}
          relationshipReadOnly={true}
          submitButtonClick={onSubmitGeneratedContact}
          showHelpMessage={showHelpMessage}
        />
      </div>
    </Dialog>
    </div>
  );
};

export default ResponsiblePartiesSection;
